import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '../api/ApiClientProvider';
import type { ApiError } from '../api/ApiError';
import { raceKeys } from './queryKeys';
import type { Organizer, RaceEventDetail, RaceEventSummary } from './types';
import { unwrap } from './unwrap';

/** Loads the publicly visible race events for discovery. */
export function usePublicRaceEvents(): UseQueryResult<RaceEventSummary[], ApiError> {
  const client = useApiClient();

  return useQuery<RaceEventSummary[], ApiError>({
    queryKey: raceKeys.list(),
    queryFn: async () => unwrap(await client.GET('/api/racing/events'), 'Could not load race events'),
  });
}

/** Loads the race events the calling organiser hosts. */
export function useMyRaceEvents(): UseQueryResult<RaceEventSummary[], ApiError> {
  const client = useApiClient();

  return useQuery<RaceEventSummary[], ApiError>({
    queryKey: raceKeys.mine(),
    queryFn: async () => unwrap(await client.GET('/api/racing/events/mine'), 'Could not load your events'),
  });
}

/** Loads one race event with its contestants, bracket and standings. */
export function useRaceEvent(raceEventId: string): UseQueryResult<RaceEventDetail, ApiError> {
  const client = useApiClient();

  return useQuery<RaceEventDetail, ApiError>({
    queryKey: raceKeys.event(raceEventId),
    enabled: raceEventId !== '',
    queryFn: async () =>
      unwrap(
        await client.GET('/api/racing/events/{raceEventId}', { params: { path: { raceEventId } } }),
        'Could not load that race event',
      ),
  });
}

/**
 * Loads the calling user's organiser profile, resolving to `null` when they have none rather than
 * treating the 404 as an error — the UI uses the absence to prompt profile creation.
 */
export function useMyOrganizer(): UseQueryResult<Organizer | null, ApiError> {
  const client = useApiClient();

  return useQuery<Organizer | null, ApiError>({
    queryKey: raceKeys.organizer(),
    queryFn: async () => {
      const result = await client.GET('/api/racing/organizers/me');
      if (result.response.status === 404) {
        return null;
      }

      return unwrap(result, 'Could not load your organiser profile');
    },
  });
}
